import { styled } from '@mui/system'
import { colorPalette } from '../../../../style/colorPalette'
import MoreVertIcon from '@mui/icons-material/MoreVertRounded'
import { useCallback, useEffect, useMemo, useState, useRef } from 'react'
import {
  ClickAwayListener,
  Divider,
  ListItemButton,
  Paper,
  Popper,
  Tooltip,
} from '@mui/material'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import { intl } from '../../../../../i18n'
import { HeaderIconButton } from './common'

type VersionReportMenuProps = {
  onDelete: () => void
  onReCalculate: () => void
  onCopyUrl: () => void
  onEvaluatePage?: () => void
  isEvaluatePageDisabled?: boolean
}

const StyledMoreVertIcon = styled(MoreVertIcon)({
  color: colorPalette.monotone[4],
  height: '16px',
  width: '16px',
})

const MenuItemListWrapper = styled('div')({
  backgroundColor: colorPalette.monotone[0],
  display: 'flex',
  flexDirection: 'column',
  padding: '4px',
  borderRadius: '4px',
  boxShadow: `0px 4px 16px 0px ${colorPalette.monotone[4]}80`,
  minWidth: '230px',
  width: 'max-content',
})

const MenuItemListHeader = styled('div')({
  color: colorPalette.monotone[5],
  fontSize: '12px',
  fontWeight: 600,
  padding: '8px',
})

const MenuItemListDivider = styled(Divider)({
  borderColor: colorPalette.monotone[2],
})

const MenuItemListArea = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  padding: '8px',
  gap: '4px',
})

const MenuItem = styled(ListItemButton)(({ alert }: { alert?: boolean }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  cursor: 'pointer',
  color: alert ? colorPalette.pink[5] : colorPalette.monotone[4],
  fontSize: '12px',
  whiteSpace: 'nowrap',
}))

const COPY_FEEDBACK_DURATION_MS = 1000

type MenuItemListProps = VersionReportMenuProps & {
  onCloseMenu: () => void
}

const MenuItemList = ({
  onDelete,
  onReCalculate,
  onCopyUrl,
  onEvaluatePage,
  isEvaluatePageDisabled,
  onCloseMenu,
}: MenuItemListProps) => {
  const [copied, setCopied] = useState(false)
  const timerRef = useRef<ReturnType<typeof setTimeout>>()

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [])

  const handleCopyUrl = useCallback(() => {
    if (copied) return
    onCopyUrl()
    setCopied(true)
    timerRef.current = setTimeout(() => {
      onCloseMenu()
    }, COPY_FEEDBACK_DURATION_MS)
  }, [copied, onCopyUrl, onCloseMenu])
  return (
    <MenuItemListWrapper>
      <MenuItemListHeader>
        {intl.formatMessage({ id: 'menu' })}
      </MenuItemListHeader>
      <MenuItemListDivider />
      <MenuItemListArea>
        <MenuItem onClick={onReCalculate}>
          {intl.formatMessage({ id: 'versionReport.recalculate.title' })}
        </MenuItem>
        <MenuItem onClick={handleCopyUrl}>
          {copied ? (
            <>
              <CheckRoundedIcon sx={{ fontSize: 14 }} />
              {intl.formatMessage({
                id: 'versionReport.menu.copyUrl.copied',
              })}
            </>
          ) : (
            intl.formatMessage({ id: 'versionReport.menu.copyUrl' })
          )}
        </MenuItem>
      </MenuItemListArea>
      {onEvaluatePage && (
        <>
          <MenuItemListDivider />
          <MenuItemListArea>
            <MenuItem
              disabled={isEvaluatePageDisabled}
              onClick={() => {
                onCloseMenu()
                onEvaluatePage()
              }}
            >
              {intl.formatMessage({
                id: 'versionReport.evaluate.allRows',
              })}
            </MenuItem>
          </MenuItemListArea>
        </>
      )}
      <MenuItemListDivider />
      <MenuItemListArea>
        <MenuItem onClick={onDelete} alert={true}>
          {intl.formatMessage({ id: 'versionReport.delete.title' })}
        </MenuItem>
      </MenuItemListArea>
    </MenuItemListWrapper>
  )
}

export const VersionReportMenu = ({
  onDelete,
  onReCalculate,
  onCopyUrl,
  onEvaluatePage,
  isEvaluatePageDisabled,
}: VersionReportMenuProps) => {
  const buttonRef = useRef<HTMLButtonElement>(null)
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
  const active = useMemo(() => Boolean(anchorEl), [anchorEl])
  const closeMenu = useCallback(() => setAnchorEl(null), [])
  const toggleMenu = useCallback(() => {
    setAnchorEl(prev => (prev ? null : buttonRef.current))
  }, [])
  return (
    <>
      <Tooltip title={intl.formatMessage({ id: 'versionReport.tooltip.menu' })}>
        <HeaderIconButton ref={buttonRef} onClick={toggleMenu}>
          <StyledMoreVertIcon />
        </HeaderIconButton>
      </Tooltip>
      <Popper
        open={active}
        anchorEl={anchorEl}
        placement="bottom-end"
        style={{ zIndex: 10 }}
      >
        <ClickAwayListener onClickAway={closeMenu}>
          <Paper>
            <MenuItemList
              onDelete={onDelete}
              onReCalculate={onReCalculate}
              onCopyUrl={onCopyUrl}
              onEvaluatePage={onEvaluatePage}
              isEvaluatePageDisabled={isEvaluatePageDisabled}
              onCloseMenu={closeMenu}
            />
          </Paper>
        </ClickAwayListener>
      </Popper>
    </>
  )
}
